import type { UserRole } from '@sfa/shared';
import {
  Plus, Boxes, Building2, CalendarDays, ChevronDown, FileSpreadsheet, Home, LayoutDashboard, LogOut, MapPin,
  BarChart3, Coins, Menu, Moon, Package, PieChart, Percent, Phone, Receipt, Route, Ruler, ShoppingCart, Store, Sun, Tags, Target, Truck, User, Users, Warehouse, X,
} from 'lucide-react';
import * as React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { BeatChangeSheet } from '@/components/beat/BeatChangeSheet';
import { NotificationBell } from '@/components/layout/NotificationBell';
import { ToastDock } from '@/components/layout/ToastDock';
import { Badge, Button } from '@/components/ui';
import { useMyBeatToday, useRequestBeatChange } from '@/hooks/useMyBeatToday';
import { useRealtime } from '@/hooks/useRealtime';
import { resolveImageUrl } from '@/lib/api';
import { cn, initials } from '@/lib/utils';
import { useAuth } from '@/store/auth';

/**
 * Application shell: sidebar, top bar, content area.
 *
 * Navigation is data-driven and filtered by role. That filtering is a
 * convenience only — every route is independently guarded, and the API
 * enforces permissions again server-side. Hiding a link is never the control.
 */

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: UserRole[];
  end?: boolean;
}

interface NavSection {
  heading?: string;
  items: NavItem[];
}

const ALL: UserRole[] = ['SUPER_ADMIN', 'SALES_EXECUTIVE', 'RETAILER'];
const STAFF: UserRole[] = ['SUPER_ADMIN', 'SALES_EXECUTIVE'];
const ADMIN: UserRole[] = ['SUPER_ADMIN'];
const SALES_EXEC: UserRole[] = ['SALES_EXECUTIVE'];
// Vehicle Planning Phase 5D — deliberately its own explicit list, never
// folded into STAFF/ALL: a Driver Portal item must never leak onto an
// Admin/Sales Executive/Retailer sidebar by inheriting a role array meant
// for someone else, and vice versa.
const DRIVER_ONLY: UserRole[] = ['DRIVER'];
// Same nav item as NON_SALES_EXEC's "Orders" below, split only so the label
// can read "Order History" for Sales Executive without touching Admin/Retailer.
const NON_SALES_EXEC: UserRole[] = ['SUPER_ADMIN', 'RETAILER'];

const NAVIGATION: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ALL, end: true },
      // Vehicle Planning Phase 5D. DRIVER's landing route is actually `/`
      // (DashboardPage redirects to /driver internally), but the nav link
      // itself points straight at /driver so it stays highlighted while a
      // driver is anywhere in the portal, matching every other role's item
      // pointing directly at its own landing page.
      { to: '/driver', label: 'My Deliveries', icon: Truck, roles: DRIVER_ONLY, end: true },
      { to: '/attendance', label: 'Attendance', icon: Phone, roles: STAFF, end: true },
      { to: '/my-beat', label: 'My Beat Today', icon: CalendarDays, roles: SALES_EXEC, end: true },
      { to: '/orders', label: 'Order History', icon: ShoppingCart, roles: SALES_EXEC },
      { to: '/orders', label: 'Orders', icon: ShoppingCart, roles: NON_SALES_EXEC },
    ],
  },
  {
    heading: 'Masters',
    items: [
      { to: '/products', label: 'Products', icon: Package, roles: ALL },
      { to: '/retailers', label: 'Retailers', icon: Store, roles: STAFF },
      { to: '/sales-executives', label: 'Sales Executives', icon: Users, roles: ADMIN },
      { to: '/vehicles', label: 'Vehicles', icon: Truck, roles: ADMIN },
      { to: '/drivers', label: 'Drivers', icon: User, roles: ADMIN },
      { to: '/godowns', label: 'Godowns', icon: Warehouse, roles: ADMIN },
    ],
  },
  {
    heading: 'Catalogue setup',
    items: [
      { to: '/masters/categories', label: 'Categories', icon: Tags, roles: ADMIN },
      { to: '/masters/brands', label: 'Brands', icon: Boxes, roles: ADMIN },
      { to: '/masters/units', label: 'Units', icon: Ruler, roles: ADMIN },
      { to: '/masters/tax-rates', label: 'GST Rates', icon: Percent, roles: ADMIN },
    ],
  },
  {
    heading: 'Field structure',
    items: [
      { to: '/masters/areas', label: 'Areas', icon: Building2, roles: ADMIN },
      { to: '/masters/routes', label: 'Routes / Beats', icon: MapPin, roles: ADMIN },
      { to: '/settings/company', label: 'Company Settings', icon: Building2, roles: ADMIN },
    ],
  },
  {
    heading: 'Delivery',
    items: [
      // Operational, not master data — Vehicle Master (the vehicle roster
      // itself) stays under Masters; this is the actual delivery-run
      // planning workflow built on top of it.
      { to: '/vehicle-plans', label: 'Vehicle Plans', icon: Route, roles: ADMIN },
      { to: '/vehicle-plan-summary', label: 'Vehicle Plan Summary', icon: FileSpreadsheet, roles: ADMIN },
    ],
  },
  {
    heading: 'Performance',
    items: [
      { to: '/reports', label: 'Sales Reports', icon: BarChart3, roles: STAFF },
      { to: '/order-summary', label: 'Order Summary', icon: PieChart, roles: ADMIN },
      { to: '/payment-summary', label: 'Payment / Collection Summary', icon: Receipt, roles: STAFF },
      { to: '/targets', label: 'Targets', icon: Target, roles: STAFF },
      { to: '/incentives', label: 'Incentives', icon: Coins, roles: STAFF },
    ],
  },
];

/**
 * "Today's beat", visible at the very top of the Sales Executive mobile
 * experience on every page — not just My Beat Today. Reuses the exact same
 * query/mutation/sheet that page uses (useMyBeatToday, useRequestBeatChange,
 * BeatChangeSheet), so there is one Beat-change state machine, not two, and
 * this strip can never show something the page itself would disagree with.
 *
 * Tapping the beat name only ever opens the request sheet — never switches
 * the active beat directly. That still requires Admin approval, exactly as
 * My Beat Today already enforces.
 */
function BeatTopBar() {
  const query = useMyBeatToday();
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const requestChange = useRequestBeatChange(() => setSheetOpen(false));

  if (query.isLoading) {
    return (
      <div className="border-b border-border bg-muted/30 px-4 py-2 lg:hidden">
        <div className="h-4 w-36 animate-pulse rounded bg-muted" />
      </div>
    );
  }

  const data = query.data;

  if (query.isError || !data) return null;

  const canRequestChange = data.punchedIn && data.pendingRequest === null;

  return (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/30 px-4 py-2 lg:hidden">
        <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Today's beat
        </span>

        {data.pendingRequest ? (
          <Badge tone="warning" className="min-w-0 truncate">
            Pending: {data.pendingRequest.requestedRouteName}
          </Badge>
        ) : data.activeBeat && canRequestChange ? (
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className="flex min-w-0 items-center gap-1 rounded-md px-2 py-1 text-sm font-medium transition-colors hover:bg-muted"
          >
            <span className="truncate">{data.activeBeat.name}</span>
            <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
          </button>
        ) : (
          <span className="truncate text-sm text-muted-foreground">
            {data.activeBeat?.name ?? (data.punchedIn ? 'No active beat' : 'Punch in first')}
          </span>
        )}
      </div>

      {sheetOpen && (
        <BeatChangeSheet
          pool={data.allowedBeatPool}
          activeBeatId={data.activeBeat?.id ?? null}
          pending={requestChange.isPending}
          onCancel={() => setSheetOpen(false)}
          onSubmit={(requestedRouteId, reason) => requestChange.mutate({ requestedRouteId, reason })}
        />
      )}
    </>
  );
}

function useTheme() {
  const [isDark, setIsDark] = React.useState(() => document.documentElement.classList.contains('dark'));

  const toggle = React.useCallback(() => {
    setIsDark((previous) => {
      const next = !previous;
      document.documentElement.classList.toggle('dark', next);
      localStorage.setItem('sfa-theme', next ? 'dark' : 'light');

      return next;
    });
  }, []);

  return { isDark, toggle };
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const user = useAuth((state) => state.user);
  const logout = useAuth((state) => state.logout);
  const { isDark, toggle } = useTheme();
  // Opens the Server-Sent Events stream for the life of the session.
  useRealtime();
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  // Close the mobile drawer on navigation, or it covers the page just opened.
  React.useEffect(() => {
    setMobileOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  const sections = React.useMemo(
    () =>
      NAVIGATION.map((section) => ({
        ...section,
        items: section.items.filter((item) => user && item.roles.includes(user.role)),
      })).filter((section) => section.items.length > 0),
    [user],
  );

  // Staff (Admin + Sales Executive) both get the persistent mobile Home bar
  // back to their own dashboard route. Retailer keeps its own separate
  // portal (RetailerDashboard) and was never in scope for this.
  const showMobileHome = !!user && STAFF.includes(user.role);

  return (
    <div className="flex min-h-screen bg-background">
      {/* Backdrop for the mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-sidebar text-sidebar-foreground transition-transform lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 shrink-0 items-center justify-between border-b border-white/10 px-4">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">Distribution SFA</p>
            <p className="truncate text-[11px] text-sidebar-muted">Order Management</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-sidebar-foreground hover:bg-white/10 lg:hidden"
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X />
          </Button>
        </div>

        <nav className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3">
          {sections.map((section, index) => (
            <div key={section.heading ?? index} className="mb-1">
              {section.heading && (
                <p className="eyebrow px-3 pb-1.5 pt-4 text-[10px] text-sidebar-muted">
                  {section.heading}
                </p>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'mb-0.5 flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-sidebar-accent font-medium text-white'
                        : 'text-sidebar-foreground hover:bg-white/10 hover:text-white',
                    )
                  }
                >
                  <item.icon className="size-4 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-white/10 p-3">
          <p className="text-[11px] text-sidebar-muted">
            Signed in as <span className="font-medium text-white">{user?.name}</span>
          </p>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-border bg-card/95 px-4 backdrop-blur">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </Button>

          {/* Mobile-only New Order CTA for Sales Executive & Admin/Super Admin.
              Desktop keeps its existing navigation; this is hidden at lg and up.
              `default` variant (solid primary) makes it the one button in this
              bar that isn't ghost/outline, so it reads as the primary action. */}
          {user && ['SUPER_ADMIN', 'SALES_EXECUTIVE'].includes(user.role) && (
            <Button
              variant="default"
              size="sm"
              className="lg:hidden h-11 shrink-0 gap-1.5 rounded-full px-3 text-sm font-semibold"
              onClick={() => navigate('/orders/new')}
              aria-label="New order"
            >
              <Plus className="size-4 shrink-0" />
              <span>New Order</span>
            </Button>
          )}

          <div className="ml-auto flex items-center gap-1.5">
            <Button variant="ghost" size="icon" onClick={toggle} aria-label="Toggle theme">
              {isDark ? <Sun /> : <Moon />}
            </Button>

            <NotificationBell />

            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-accent"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <span className="grid size-8 shrink-0 place-items-center overflow-hidden rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                  {user?.avatarUrl ? (
                    <img
                      src={resolveImageUrl(user.avatarUrl)}
                      alt=""
                      className="size-full object-cover"
                      // A stored reference can go stale — fall back to
                      // initials rather than a broken-image icon.
                      onError={(event) => {
                        const target = event.currentTarget as HTMLImageElement;
                        target.style.display = 'none';
                        target.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <span className={user?.avatarUrl ? 'hidden' : undefined}>{initials(user?.name)}</span>
                </span>
                <span className="hidden min-w-0 sm:block">
                  <span className="block truncate text-sm font-medium leading-tight">{user?.name}</span>
                  <span className="block truncate text-[11px] leading-tight text-muted-foreground">
                    {user?.role.replace('_', ' ').toLowerCase()}
                  </span>
                </span>
                <ChevronDown className="size-4 text-muted-foreground" />
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} aria-hidden />
                  <div
                    role="menu"
                    className="absolute right-0 z-50 mt-1 w-56 animate-fade-in rounded-md border border-border bg-popover p-1 shadow-lg"
                  >
                    <div className="border-b border-border px-3 py-2">
                      <p className="truncate text-sm font-medium">{user?.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                      <Badge tone="info" className="mt-1.5">
                        {user?.role.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void logout()}
                      className="mt-1 flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm text-destructive transition-colors hover:bg-destructive/10"
                    >
                      <LogOut className="size-4" />
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Space the notifications occupy while they show — see ToastDock. */}
        <ToastDock />

        {/* Very top of the Sales Executive mobile experience, on every page —
            see BeatTopBar's own doc comment for why it reuses My Beat Today's
            state instead of maintaining its own. */}
        {user?.role === 'SALES_EXECUTIVE' && <BeatTopBar />}

        {/*
          Extra bottom clearance only where the fixed mobile Home bar below
          actually renders (Admin + Sales Executive, <lg) — otherwise its
          56px bar plus the phone's safe-area inset would sit on top of the
          page's last row of content.
        */}
        <main
          className={cn(
            'flex-1 p-4 lg:p-6',
            showMobileHome && 'pb-24 lg:pb-6',
          )}
        >
          {children}
        </main>
      </div>

      {/*
        Persistent mobile Home — Admin + Sales Executive (Retailer has its
        own separate portal). One thumb-reach back to the dashboard from
        anywhere in the app, no menu to open first. Lives in AppShell (not
        per-page) so every current and future route for both roles inherits
        it automatically.
      */}
      {showMobileHome && (
        <nav
          className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 backdrop-blur lg:hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          aria-label="Return to dashboard"
        >
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              cn(
                'flex h-14 w-full items-center justify-center gap-2 text-sm font-medium transition-colors active:bg-accent',
                isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            <Home className="size-5 shrink-0" />
            Home
          </NavLink>
        </nav>
      )}
    </div>
  );
}
